import React, { useState, useEffect } from 'react';
import { usePermissions } from '../../hooks/usePermissions';
import { PERMISSIONS } from '../../constants/permissions';
import { auth } from '../../firebase/firebaseConfig';
import MasterDataPanel from '../../components/settings/MasterDataPanel';
import VehicleTypeConfig from '../../components/settings/VehicleTypeConfig';
import LoadingRuleConfig from '../../components/settings/LoadingRuleConfig';
import DockConfig from '../../components/settings/DockConfig';
import DockTypeConfig from '../../components/settings/DockTypeConfig';
import NumberRangeConfig from '../../components/settings/NumberRangeConfig';
import TransporterTypeConfig from '../../components/settings/TransporterTypeConfig';
import TransportModeConfig from '../../components/settings/TransportModeConfig';
import SystemParametersConfig from '../../components/settings/SystemParametersConfig';
import CarrierPerformanceConfig from '../../components/settings/CarrierPerformanceConfig';
import '../../styles/nav-tree.css';

// --- Tree Data Structure ---
const TREE_NODES = {
    MASTER_DATA: {
        id: 'master_data',
        label: 'Veri Yönetimi',
        icon: '📂',
        children: [
            { id: 'general_settings', label: 'Genel Ayarlar', icon: '⚙️', view: 'general_settings' }, // New
            { id: 'factories', label: 'Fabrikalar', icon: '🏭', view: 'factories' },
            { id: 'door_groups', label: 'Rampa Grupları', icon: '🚪', view: 'door_groups' },
            { id: 'docks', label: 'Rampalar', icon: '⚓', view: 'docks' },
            { id: 'dock_types', label: 'Rampa Tipleri', icon: '🏷️', view: 'dock_types' },
            { id: 'transporter_types', label: 'Tedarikçi Türleri', icon: '🚚', view: 'transporter_types' },
            { id: 'transport_modes', label: 'Nakliye Türleri', icon: '📦', view: 'transport_modes' }, // New
            { id: 'number_ranges', label: 'Numara Aralıkları', icon: '🔢', view: 'number_ranges' },
            { id: 'carrier_performance', label: 'Performans Kriterleri', icon: '📈', view: 'carrier_performance' }
        ]
    },
    VEHICLE_CONFIG: {
        id: 'vehicle_config',
        label: 'Araç Yönetimi',
        icon: '📂',
        children: [
            { id: 'vehicle_types', label: 'Araç Tipleri', icon: '🚛', view: 'vehicle_types' },
            { id: 'loading_rules', label: 'Yükleme Kuralları', icon: '⏱️', view: 'loading_rules' }
        ]
    }
};

const Settings = () => {
    const [localUserData, setLocalUserData] = useState(null);
    const [authLoading, setAuthLoading] = useState(true);

    // Default Selection: None (empty state)
    const [activeNode, setActiveNode] = useState(null);

    // persist expandedFolders
    const [expandedFolders, setExpandedFolders] = useState([]);

    // We no longer persist activeView to localStorage to ensure fresh start every time
    // But we might want to keep the useEffect if we want to prevent errors if code elsewhere relies on it, 
    // though the user specifically asked for "closed on entry".
    // I will remove the localStorage read usage above. 

    /* 
       We kept the useEffects below for interaction, but since we start with null/empty,
       the page will be blank on load.
    */

    useEffect(() => {
        localStorage.setItem('settingsActiveView', activeNode || '');
    }, [activeNode]);

    useEffect(() => {
        localStorage.setItem('settingsExpandedFolders', JSON.stringify(expandedFolders));
    }, [expandedFolders]);

    // Auto-expand folder if activeNode exists but folder is closed (Optional UX improvement)
    useEffect(() => {
        if (activeNode) {
            const parentKey = Object.keys(TREE_NODES).find(key =>
                TREE_NODES[key].children.some(child => child.id === activeNode)
            );
            if (parentKey) {
                const folderId = TREE_NODES[parentKey].id;
                setExpandedFolders(prev => {
                    if (!prev.includes(folderId)) return [...prev, folderId];
                    return prev;
                });
            }
        }
    }, [activeNode]);

    useEffect(() => {
        const unsubscribe = auth.onAuthStateChanged(async (currentUser) => {
            if (currentUser) {
                const userData = { email: currentUser.email, uid: currentUser.uid };
                try {
                    const { getUserData } = await import('../../services/auth/authService');
                    const firestoreData = await getUserData(currentUser.uid);
                    setLocalUserData({ ...userData, ...firestoreData });
                } catch (e) {
                    console.error("User data fetch failed", e);
                    setLocalUserData(userData);
                }
            }
            setAuthLoading(false);
        });
        return () => unsubscribe();
    }, []);

    const { user, can, loading: permLoading } = usePermissions(localUserData);
    const loading = authLoading || permLoading;

    if (loading) {
        return (
            <div className="flex justify-center items-center" style={{ height: '80vh' }}>
                <div className="badge badge-pending">Yetkiler Kontrol Ediliyor...</div>
            </div>
        );
    }

    const isSuperUser = user?.email === 'mert.akan@zaparmy.com.tr';
    if (!isSuperUser && !can(PERMISSIONS.SETTINGS, PERMISSIONS.VIEW)) {
        return (
            <div className="container mt-xl text-center" style={{ padding: '80px 20px' }}>
                <div style={{ fontSize: '3rem', marginBottom: '20px' }}>🔒</div>
                <h2 style={{ color: 'var(--text-secondary)' }}>Yetkisiz Erişim</h2>
                <p className="text-secondary">Bu sayfayı görüntüleme yetkiniz bulunmamaktadır.</p>
            </div>
        );
    }

    // Toggle Folder Expansion
    const toggleFolder = (folderId) => {
        if (expandedFolders.includes(folderId)) {
            setExpandedFolders(expandedFolders.filter(id => id !== folderId));
        } else {
            setExpandedFolders([...expandedFolders, folderId]);
        }
    };

    // Render Content based on activeNode
    const renderContent = () => {
        switch (activeNode) {
            case 'general_settings':
                return <SystemParametersConfig user={user} />;
            case 'factories':
                return <MasterDataPanel user={user} initialActiveLevel="factories" />;
            case 'door_groups':
                return <MasterDataPanel user={user} initialActiveLevel="door_groups" />;
            case 'docks':
                return <DockConfig user={user} />;
            case 'dock_types':
                return <DockTypeConfig user={user} />;
            case 'transporter_types':
                return <TransporterTypeConfig user={user} />;
            case 'transport_modes':
                return <TransportModeConfig user={user} />;
            case 'number_ranges':
                return <NumberRangeConfig user={user} />;
            case 'carrier_performance':
                return <CarrierPerformanceConfig user={user} />;
            case 'vehicle_types':
                return <VehicleTypeConfig user={user} />;
            case 'loading_rules':
                return <LoadingRuleConfig user={user} />;
            case 'loading_rules':
                return <LoadingRuleConfig user={user} />;
            default:
                return (
                    <div className="flex flex-col items-center justify-center h-full opacity-30 text-center py-20">
                        <div style={{ fontSize: '4rem', marginBottom: '16px' }}>👈</div>
                        <h3 className="text-xl">Bir ayar menüsü seçiniz</h3>
                        <p>Soldaki menüden işlem yapmak istediğiniz kategoriyi seçin.</p>
                    </div>
                );
        }
    };

    // Helper to get active breadcrumb title
    const getActiveTitle = () => {
        if (!activeNode) return 'Ayarlar';
        for (const key in TREE_NODES) {
            const folder = TREE_NODES[key];
            const found = folder.children.find(child => child.id === activeNode);
            if (found) return `${folder.label} / ${found.label}`;
        }
        return 'Ayarlar';
    };

    return (
        <div className="container-fluid mt-lg" style={{ paddingBottom: '50px' }}>
            {/* Header */}
            {/* Header Removed as per request */}

            {/* Split Layout */}
            <div style={{ display: 'flex', gap: 'var(--spacing-lg)', alignItems: 'flex-start' }}>

                {/* Left Sidebar - Tree Menu */}
                <div className="card-premium glow-theme p-md" style={{ width: '280px', flexShrink: 0, minHeight: '400px' }}>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--spacing-sm)' }}>
                        {Object.values(TREE_NODES).map(folder => (
                            <div key={folder.id}>
                                {/* Folder Header */}
                                <div
                                    className="nav-folder"
                                    onClick={() => toggleFolder(folder.id)}
                                    style={{ color: 'var(--text-primary)' }}
                                >
                                    <span style={{ marginRight: '8px', fontSize: '1.1rem', transform: expandedFolders.includes(folder.id) ? 'rotate(90deg)' : 'rotate(0deg)', transition: 'transform 0.2s', color: 'var(--primary-color)' }}>▶</span>
                                    <span style={{ marginRight: '8px' }}>{folder.icon}</span>
                                    <span style={{ fontWeight: 600, fontSize: '0.95rem' }}>{folder.label}</span>
                                </div>

                                {/* Folder Children */}
                                {expandedFolders.includes(folder.id) && (
                                    <div style={{ paddingLeft: '28px', marginTop: '4px', display: 'flex', flexDirection: 'column', gap: '4px' }}>
                                        {folder.children.map(child => (
                                            <div
                                                key={child.id}
                                                className={`nav-item ${activeNode === child.id ? 'active' : ''}`}
                                                onClick={() => setActiveNode(child.id)}
                                                style={{ color: activeNode === child.id ? 'var(--text-primary)' : 'var(--text-secondary)' }}
                                            >
                                                <span style={{ marginRight: '8px', fontSize: '1.1rem' }}>{child.icon}</span>
                                                {child.label}
                                            </div>
                                        ))}
                                    </div>
                                )}
                            </div>
                        ))}
                    </div>
                </div>

                {/* Right Content Area */}
                <div className="card-premium glow-theme p-lg" style={{ flex: 1, minHeight: '500px' }}>
                    {/* Content Header - Only show if activeNode is selected */}
                    {activeNode && (
                        <div className="mb-lg pb-md" style={{ borderBottom: '1px solid var(--border-color)' }}>
                            <h2 style={{ fontSize: '1.5rem', marginBottom: '4px', color: 'var(--text-primary)' }}>{getActiveTitle().split('/')[1]?.trim()}</h2>
                            <span style={{ color: 'var(--text-secondary)', fontSize: '0.875rem' }}>{getActiveTitle()}</span>
                        </div>
                    )}

                    {renderContent()}
                </div>
            </div>
        </div>
    );
};

export default Settings;
